import * as THREE from 'three';
import { GRID_COLS } from '../game/config';
import type { LocationDefinition, PathCell, TextureMaps } from '../game/types';

const textureLoader = new THREE.TextureLoader();

function loadMap(path: string, repeatX: number, repeatY: number): THREE.Texture {
  const texture = textureLoader.load(path);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export const MAPS: TextureMaps = {
  desert: loadMap('/textures/desert-sand-1k.jpg', 8, 7),
  snow: loadMap('/textures/snow-1k.jpg', 8, 7),
  canyon: loadMap('/textures/red-sandstone-1k.jpg', 8, 7),
  brick: loadMap('/textures/brick-wall-1k.jpg', 1.15, 0.85),
  rock: loadMap('/textures/rock-path-1k.jpg', 1.15, 1.15),
};

function rowCells(row: number): PathCell[] {
  return Array.from({ length: GRID_COLS }, (_, col) => ({ col, row }));
}

function columnCells(col: number, startRow: number, endRow: number): PathCell[] {
  return Array.from({ length: endRow - startRow + 1 }, (_, index) => ({ col, row: startRow + index }));
}

export const LOCATIONS: LocationDefinition[] = [
  {
    key: 'desert', name: 'ПУСТЫННЫЙ РУБЕЖ', kicker: 'SANDS OF OMEGA',
    groundMap: MAPS.desert, rockMap: MAPS.rock, background: 0x211711, fieldColor: 0xb58a59, fieldEmissive: 0x4b2d1d,
    gridMajor: 0x6f4933, gridMinor: 0x4e3529, frame: 0xf1a35a, brickColor: 0xb86543, rockColor: 0x737c7f,
    keyLight: 0xffc58a, fillLight: 0xd47d4d, ambient: 0x6a4b36, hemisphereSky: 0xf0b982, hemisphereGround: 0x3d2b22,
    decor: 'desert',
    enemySpawnPoints: [{ col: 0, row: 0 }, { col: 6, row: 0 }, { col: 9, row: 1 }, { col: 13, row: 1 }],
    enemyExplorePoints: [{ col: 1, row: 3 }, { col: 6, row: 3 }, { col: 10, row: 5 }, { col: 12, row: 7 }],
    riverCells: rowCells(5),
    bridgeCells: [{ col: 2, row: 5 }, { col: 7, row: 5 }, { col: 10, row: 5 }],
  },
  {
    key: 'snow', name: 'СЕВЕРНЫЙ ПЕРИМЕТР', kicker: 'WHITEOUT PROTOCOL',
    groundMap: MAPS.snow, rockMap: MAPS.rock, background: 0x10232d, fieldColor: 0xd9e7e8, fieldEmissive: 0x405e67,
    gridMajor: 0x477d89, gridMinor: 0x2d5968, frame: 0x9debf0, brickColor: 0x8ea4a6, rockColor: 0x899598,
    keyLight: 0xd9f4ff, fillLight: 0x7fb4d0, ambient: 0x557d8d, hemisphereSky: 0xd4f5ff, hemisphereGround: 0x1f3740,
    decor: 'snow',
    enemySpawnPoints: [{ col: 2, row: 0 }, { col: 7, row: 0 }, { col: 12, row: 1 }],
    enemyExplorePoints: [{ col: 3, row: 2 }, { col: 8, row: 3 }, { col: 11, row: 5 }, { col: 5, row: 7 }],
    riverCells: [...rowCells(3), ...rowCells(7)],
    bridgeCells: [{ col: 3, row: 3 }, { col: 8, row: 3 }, { col: 5, row: 7 }],
  },
  {
    key: 'canyon', name: 'КРАСНЫЙ КАНЬОН', kicker: 'DUST & IRON',
    groundMap: MAPS.canyon, rockMap: MAPS.canyon, background: 0x24140f, fieldColor: 0x9b5e43, fieldEmissive: 0x492318,
    gridMajor: 0xa56849, gridMinor: 0x6c392c, frame: 0xffb35d, brickColor: 0x8f4938, rockColor: 0x6d7679,
    keyLight: 0xffbd7a, fillLight: 0xc65d3c, ambient: 0x6d382a, hemisphereSky: 0xc47759, hemisphereGround: 0x291711,
    decor: 'canyon',
    enemySpawnPoints: [{ col: 1, row: 0 }, { col: 5, row: 1 }, { col: 10, row: 0 }, { col: 13, row: 1 }],
    enemyExplorePoints: [{ col: 2, row: 3 }, { col: 7, row: 3 }, { col: 11, row: 5 }, { col: 4, row: 7 }],
    riverCells: columnCells(7, 1, 9),
    bridgeCells: [{ col: 7, row: 3 }, { col: 7, row: 7 }],
  },
];

export function locationForLevel(level: number): LocationDefinition {
  return LOCATIONS[(level - 1) % LOCATIONS.length];
}
